/**
 * OveragesTable — Usage overage tracking table
 *
 * Self-contained: reads directly from useBillingStore (overages, isLoadingOverages).
 * Shows which accounts have exceeded their plan limits for the current billing period.
 */
import { cn } from '@respectly/ui';
import { Card, CardContent, Button, Skeleton } from '@respectly/ui';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  FileText,
  Mail,
  MessageSquare,
  Phone,
  RefreshCw,
  TrendingUp,
  Building2,
} from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useBillingStore } from '../../stores/useBillingStore';

// ============================================
// Formatters
// ============================================

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatNumber(n: number) {
  return new Intl.NumberFormat('en-US').format(n);
}

// ============================================
// Type badge config
// ============================================

type OverageType = 'sms' | 'email' | 'documents' | 'voice';

const typeConfig: Record<
  OverageType,
  { label: string; icon: React.ElementType; className: string; iconClass: string }
> = {
  sms: {
    label: 'SMS',
    icon: MessageSquare,
    className:
      'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200 dark:bg-violet-950/40 dark:text-violet-400 dark:ring-violet-800/60',
    iconClass: 'text-violet-600 dark:text-violet-400',
  },
  email: {
    label: 'Email',
    icon: Mail,
    className:
      'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200 dark:bg-sky-950/40 dark:text-sky-400 dark:ring-sky-800/60',
    iconClass: 'text-sky-600 dark:text-sky-400',
  },
  documents: {
    label: 'Documents',
    icon: FileText,
    className:
      'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:ring-amber-800/60',
    iconClass: 'text-amber-600 dark:text-amber-400',
  },
  voice: {
    label: 'Voice',
    icon: Phone,
    className:
      'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:ring-emerald-800/60',
    iconClass: 'text-emerald-600 dark:text-emerald-400',
  },
};

// ============================================
// Stat pill
// ============================================

function StatPill({
  label,
  value,
  color = 'slate',
}: {
  label: string;
  value: string;
  color?: 'slate' | 'orange' | 'red';
}) {
  const colorClass = {
    slate: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300',
    orange:
      'bg-orange-50 dark:bg-orange-950/40 text-orange-700 dark:text-orange-400 ring-1 ring-inset ring-orange-200 dark:ring-orange-800/60',
    red: 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400 ring-1 ring-inset ring-red-200 dark:ring-red-800/60',
  }[color];

  return (
    <div className={cn('flex items-center gap-2 rounded-lg px-3 py-2', colorClass)}>
      <span className="text-[11px] font-medium opacity-70">{label}</span>
      <span className="text-sm font-bold tabular-nums">{value}</span>
    </div>
  );
}

// ============================================
// Skeleton
// ============================================

export function OveragesTableSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        {[100, 140, 90].map((w, i) => (
          <Skeleton key={i} className={`h-9 w-${w === 100 ? '28' : w === 140 ? '36' : '24'} rounded-lg`} />
        ))}
      </div>
      <Card className="border-slate-200/60 dark:border-slate-700/60">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-800">
                  {['Account', 'Type', 'Period', 'Used / Limit', 'Over By', '% Over', 'Est. Charge', ''].map(
                    (col, i) => (
                      <th
                        key={i}
                        className={cn(
                          'py-2.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400',
                          i === 0 ? 'px-5 text-left' : i >= 3 ? 'px-4 text-right' : 'px-4 text-left'
                        )}
                      >
                        {col}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-800/50">
                {Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-5 py-3.5">
                      <Skeleton className="h-3.5 w-28 mb-1" />
                      <Skeleton className="h-3 w-16" />
                    </td>
                    <td className="px-4 py-3.5">
                      <Skeleton className="h-5 w-20 rounded-full" />
                    </td>
                    <td className="px-4 py-3.5">
                      <Skeleton className="h-3.5 w-16" />
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <Skeleton className="h-3.5 w-24 ml-auto" />
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <Skeleton className="h-3.5 w-14 ml-auto" />
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <Skeleton className="h-5 w-12 rounded-full ml-auto" />
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <Skeleton className="h-3.5 w-16 ml-auto" />
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <Skeleton className="h-6 w-20 ml-auto" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================
// Component
// ============================================

export function OveragesTable({ className }: { className?: string }) {
  const navigate = useNavigate();
  const { overages, isLoadingOverages, loadOverages } = useBillingStore();

  const currentPeriod = new Date().toISOString().slice(0, 7); // 'YYYY-MM'

  // Derived stats
  const uniqueAccounts = new Set(overages.map((o) => o.tenantId)).size;
  const totalEstimatedCharge = overages.reduce((sum, o) => sum + (o.estimatedCharge ?? 0), 0);

  const handleViewAccount = (tenantId: string) => {
    navigate({ to: '/accounts/$id', params: { id: tenantId } });
  };

  if (isLoadingOverages) {
    return <OveragesTableSkeleton />;
  }

  if (overages.length === 0) {
    return (
      <div className={cn('flex flex-col items-center gap-4 py-20 text-center', className)}>
        <div className="relative">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 dark:bg-emerald-950/30">
            <CheckCircle2 className="h-8 w-8 text-emerald-500 dark:text-emerald-400" />
          </div>
          <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-[10px] font-bold text-white">
            ✓
          </div>
        </div>
        <div>
          <h3 className="text-base font-semibold text-slate-900 dark:text-white">
            No usage overages
          </h3>
          <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">
            All accounts are within their plan limits for{' '}
            <span className="font-medium text-slate-600 dark:text-slate-300">{currentPeriod}</span>.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          onClick={() => loadOverages()}
        >
          <RefreshCw className="mr-1.5 h-3 w-3" />
          Refresh
        </Button>
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Summary bar */}
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-wrap items-center gap-2"
      >
        <div className="flex items-center gap-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 px-3 py-2">
          <TrendingUp className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400" />
          <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">Period</span>
          <span className="text-sm font-bold text-slate-800 dark:text-white font-mono">{currentPeriod}</span>
        </div>
        <StatPill
          label="Accounts Over Limit"
          value={String(uniqueAccounts)}
          color="orange"
        />
        <StatPill
          label="Est. Total Charges"
          value={formatCurrency(totalEstimatedCharge)}
          color="red"
        />
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto h-8 gap-1.5 text-xs text-slate-500 hover:text-slate-700"
          onClick={() => loadOverages()}
        >
          <RefreshCw className="h-3 w-3" />
          Refresh
        </Button>
      </motion.div>

      {/* Table */}
      <Card className="border-slate-200/60 dark:border-slate-700/60 shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-800/80">
                  <th className="px-5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                    Account
                  </th>
                  <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                    Type
                  </th>
                  <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                    Period
                  </th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                    Used / Limit
                  </th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                    Over By
                  </th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                    % Over
                  </th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                    Est. Charge
                  </th>
                  <th className="px-5 py-2.5 text-right" aria-label="Actions" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-800/50">
                {overages.map((overage, i) => {
                  const tc = typeConfig[overage.type as OverageType];
                  const Icon = tc?.icon ?? TrendingUp;
                  const percentOver = overage.percentOver ??
                    (overage.limit > 0 ? Math.round((overage.overage / overage.limit) * 100) : 0);
                  const isCritical = percentOver >= 50;

                  return (
                    <motion.tr
                      key={overage.id}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.15, delay: i * 0.03 }}
                      className="group transition-colors hover:bg-orange-50/20 dark:hover:bg-orange-950/5"
                    >
                      {/* Account */}
                      <td className="px-5 py-3.5">
                        <button
                          onClick={() => handleViewAccount(overage.tenantId)}
                          className="group/acct flex items-center gap-1.5 text-left"
                        >
                          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-slate-100 dark:bg-slate-800 group-hover/acct:bg-indigo-50 dark:group-hover/acct:bg-indigo-950/40 transition-colors">
                            <Building2 className="h-3 w-3 text-slate-500 dark:text-slate-400 group-hover/acct:text-indigo-600 dark:group-hover/acct:text-indigo-400 transition-colors" />
                          </div>
                          <span className="text-xs font-medium text-slate-700 dark:text-slate-300 group-hover/acct:text-indigo-600 dark:group-hover/acct:text-indigo-400 transition-colors">
                            {overage.tenantName}
                          </span>
                        </button>
                      </td>

                      {/* Type badge */}
                      <td className="px-4 py-3.5">
                        {tc ? (
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
                              tc.className
                            )}
                          >
                            <Icon className="h-3 w-3" />
                            {tc.label}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400 capitalize">{overage.type}</span>
                        )}
                      </td>

                      {/* Period */}
                      <td className="px-4 py-3.5 text-xs font-mono text-slate-500 dark:text-slate-400">
                        {overage.period}
                      </td>

                      {/* Used / Limit */}
                      <td className="px-4 py-3.5 text-right">
                        <span className="text-xs tabular-nums font-semibold text-red-600 dark:text-red-400">
                          {formatNumber(overage.used)}
                        </span>
                        <span className="text-xs text-slate-400 dark:text-slate-600">
                          {' '}/ {formatNumber(overage.limit)}
                        </span>
                      </td>

                      {/* Over By */}
                      <td className="px-4 py-3.5 text-right text-xs tabular-nums font-semibold text-red-600 dark:text-red-400">
                        +{formatNumber(overage.overage ?? (overage.used - overage.limit))}
                      </td>

                      {/* % Over */}
                      <td className="px-4 py-3.5 text-right">
                        <span
                          className={cn(
                            'inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums',
                            isCritical
                              ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400'
                              : 'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400'
                          )}
                        >
                          +{percentOver}%
                        </span>
                      </td>

                      {/* Est. Charge */}
                      <td className="px-4 py-3.5 text-right">
                        {(overage.estimatedCharge ?? 0) > 0 ? (
                          <span className="text-xs tabular-nums font-semibold text-amber-600 dark:text-amber-400">
                            {formatCurrency(overage.estimatedCharge ?? 0)}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400 dark:text-slate-600">—</span>
                        )}
                      </td>

                      {/* Action */}
                      <td className="px-5 py-3.5 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewAccount(overage.tenantId)}
                          className="h-6 px-2 text-[11px] text-slate-400 opacity-0 transition-all group-hover:opacity-100 hover:text-slate-700 hover:bg-slate-100 dark:hover:text-slate-200 dark:hover:bg-slate-700"
                        >
                          View Account
                        </Button>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
